import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "admin-lte/dist/css/adminlte.min.css";
import DataTable from "react-data-table-component";
import { useLocalization } from "../../../context/LocalizationContext";
import apiManager from "../../../api/apiManager";
import API_ENDPOINTS from "../../../api/apiEndpoints";
import {
  showLoader,
  hideLoader,
} from "../../../shared/Services/Loader/loaderService";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/Services/toasterService";

const DealerShipListing = () => {
  const { t: localization } = useLocalization();
  const [dealerList, setDealerList] = useState([]);
  const [gridSearch, setGridSearch] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDealerShipList();
  }, []);

  const fetchDealerShipList = async () => {
    try {
      showLoader();
      const response = await apiManager.get(API_ENDPOINTS.DEALER_GET_LIST);
      if (response.status == 200) {
        const data = response.data;
        setDealerList(data);
      } else {
        showErrorMessage(localization("failed_fetch_services"));
      }
    } catch (e) {
      showErrorMessage(e.message);
    } finally {
      hideLoader();
    }
  };

  const fetchServiceDetails = async (id) => {
    try {
      const response = await apiManager.post(API_ENDPOINTS.DEALER_GET_ID, {
        id,
      });
      if (response.status == 200) {
        const data = response.data;
        setSelectedService(data);
        setShowModal(true);
      } else {
        showErrorMessage(localization("failed_fetch_service_details"));
      }
    } catch (e) {
      showErrorMessage(e.message);
    }
  };

  const requestDeleteService = (id) => {
    setServiceToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteService = async () => {
      try {
        showLoader();
        const response = await apiManager.post(API_ENDPOINTS.DEALER_DELETE, {
          id: serviceToDelete,
        });
        if (response.status == 200) {
          showSuccessMessage(localization("dealer_deleted"));
          try {
            window.dispatchEvent(new Event("dealershipsChanged"));
            window.localStorage.setItem(
              "dealershipsVersion",
              Date.now().toString(),
            );
          } catch (_) {}
          await fetchDealerShipList();
          setShowDeleteModal(false);
          setServiceToDelete(null);
        } else {
          showErrorMessage(localization("failed_delete_dealer"));
        }
      } catch (err) {
        const apiMessage = err?.response?.data?.message;
        showErrorMessage(apiMessage || err.message || localization("failed_delete_dealer"));
        setShowDeleteModal(false);
      } finally {
        hideLoader();
      }
    };

  const editService = (id) => {
    navigate(`/services/dealership/edit/${id}`);
  };

  const columns = [
    {
      name: localization("shop_name"),
      selector: (row) => row.name,
      sortable: true,
      wrap: true,
    },
    {
      name: localization("address"),
      selector: (row) => row.address1,
      sortable: true,
      wrap: true,
    },
    {
      name: localization("city_locality"),
      selector: (row) => row.cityOrLocality,
      sortable: true,
      wrap: true,
    },
    {
      name: localization("postal_zip_code"),
      selector: (row) => row.postalCodeOrZip,
      sortable: true,
      wrap: true,
    },
    {
      name: (
        <div style={{ width: "100%", textAlign: "left" }}>
          {localization("actions")}
        </div>
      ),
      cell: (row) => (
        <div className="d-flex justify-content-center align-items-center gap-2">
          <button
            className="btn btn-sm text-white btn-blue d-flex justify-content-center align-items-center"
            onClick={() => fetchServiceDetails(row.id)}
            title={localization("view_details")}
          >
            <i className="fas fa-eye"></i>
          </button>
          <button
            className="btn btn-sm text-white btn-golden-yellow d-flex justify-content-center align-items-center"
            onClick={() => editService(row.id)}
            title={localization("edit_dealer")}
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            className="btn btn-sm text-white btn-red d-flex justify-content-center align-items-center"
            onClick={() => requestDeleteService(row.id)}
            title={localization("delete_dealer")}
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      ),

      style: { justifyContent: "flex-start" },
      ignoreRowClick: true,
      allowOverflow: true,
      width: "130px",
    },
  ];

  const customStyles = {
    table: {
      style: {
        minWidth: "900px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#f4f4f4",
        color: "#333",
        borderRight: "1px solid #ccc",
        fontSize: "16px",
        fontWeight: 600,
        whiteSpace: "nowrap",
      },
    },
    cells: {
      style: {
        borderRight: "1px solid #eee",
        fontSize: "13px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        maxWidth: "300px",
      },
    },
    rows: {
      style: {
        minHeight: "50px",
      },
    },
  };

  return (
    <>
      <div className="container-fluid content-wrapper select-vehicle-content">
        <div className="content form-card">
          <div className="page-title">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-md-6 d-flex align-items-center">
                  <h1>{localization("available_shops")}</h1>
                </div>
                <div className="col-md-6 d-flex justify-content-end">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="d-flex justify-content-end"
                      style={{
                        position: "relative",
                        width: "250px",
                        marginLeft: "auto",
                      }}
                    >
                      <input
                        type="text"
                        className="form-control"
                        placeholder={localization("search")}
                        style={{ width: "100%", paddingRight: "2.5rem" }}
                        value={gridSearch}
                        onChange={(e) => setGridSearch(e.target.value)}
                        id="dealer-grid-search"
                        aria-label={localization("search")}
                      />
                      {gridSearch && (
                        <button
                          type="button"
                          className="btn btn-link p-0 position-absolute"
                          style={{
                            right: "8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#888",
                            fontSize: "1.2rem",
                            textDecoration: "none",
                          }}
                          onClick={() => {
                            setGridSearch("");
                            setTimeout(() => {
                              document
                                .getElementById("dealer-grid-search")
                                ?.focus();
                            }, 0);
                          }}
                          tabIndex={-1}
                          aria-label={localization("clear_search")}
                        >
                          <i className="fas fa-times-circle"></i>
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => navigate("/services/dealership/create")}
                    >
                      {localization("add_shop")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid p-0">
            <div className="row justify-content-center mt-3">
              <div className="col-lg-12">
                <div className="">
                  <div className="">
                    <>
                      <DataTable
                        columns={columns}
                        data={dealerList.filter((d) => {
                          const keyword = gridSearch.toLowerCase();
                          return (
                            d.name?.toLowerCase().includes(keyword) ||
                            d.address1?.toLowerCase().includes(keyword) ||
                            d.cityOrLocality?.toLowerCase().includes(keyword) ||
                            d.postalCodeOrZip?.toLowerCase().includes(keyword)
                          );
                        })}
                        pagination={dealerList.length > 0}
                        striped
                        highlightOnHover
                        pointerOnHover
                        noDataComponent={localization("no_record_found")}
                        responsive
                        customStyles={customStyles}
                        noHeader
                      />

                      <div className="mb-3">
                        <button
                          className="btn btn-blue btn-sm"
                          onClick={() => navigate("/admin-dashboard")}
                        >
                          {localization("back")}
                        </button>
                      </div>
                    </>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedService && (
        <div
          className={`modal fade ${showModal ? "show d-block" : ""}`}
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  {localization("shop_details")}
                </h5>
              </div>
              <div
                className="modal-body"
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                }}
              >
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <th>{localization("name")}</th>
                      <td>{selectedService.name}</td>
                    </tr>
                    <tr>
                      <th>{localization("address_1")}</th>
                      <td>{selectedService.address1}</td>
                    </tr>
                    <tr>
                      <th>{localization("address_2")}</th>
                      <td>
                        {selectedService.address2 ||
                          localization("not_available")}
                      </td>
                    </tr>
                    <tr>
                      <th>{localization("address_3")}</th>
                      <td>
                        {selectedService.address3 ||
                          localization("not_available")}
                      </td>
                    </tr>
                    <tr>
                      <th>{localization("city")}</th>
                      <td>{selectedService.cityOrLocality}</td>
                    </tr>
                    <tr>
                      <th>{localization("state")}</th>
                      <td>{selectedService.stateOrProvinceCode}</td>
                    </tr>
                    <tr>
                      <th>{localization("zip")}</th>
                      <td>{selectedService.postalCodeOrZip}</td>
                    </tr>
                    <tr>
                      <th>{localization("labour_rate")}</th>
                      <td>{selectedService.hourlyLabourRate}</td>
                    </tr>
                    <tr>
                      <th>{localization("language")}</th>
                      <td>{selectedService.languageCode}</td>
                    </tr>
                    <tr>
                      <th>{localization("tax")}</th>
                      <td>
                        {selectedService.tax ?? localization("not_available")}
                      </td>
                    </tr>
                    <tr>
                      <th>{localization("time_zone")}</th>
                      <td>{selectedService.timeZoneCode}</td>
                    </tr>
                    <tr>
                      <th>{localization("logo")}</th>
                      <td>
                        <div
                          style={{
                            width: "200px",
                            height: "120px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={selectedService.logoPath}
                            alt={localization("logo")}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                              padding: "5px",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "/assets/images/logo/no-logo-placeholder.svg";
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                {selectedService.selectedServices?.length > 0 && (
                  <>
                    <h6 className="fw-bold mt-4 mb-3">
                      {localization("selected_services")}
                    </h6>
                    <table className="table table-striped table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>{localization("sr_no")}</th>
                          <th>{localization("service_name")}</th>
                          <th>{localization("price")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedService.selectedServices.map(
                          (service, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                {service.opCode?.trim()
                                  ? `${service.opCode} - ${service.serviceName}`
                                  : service.serviceName}
                              </td>
                              <td>${service.price}</td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  {localization("close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  {localization("confirm_delete")}
                </h5>
              </div>
              <div className="modal-body">
                {(() => {
                  const dealer = dealerList.find(
                    (d) => d.id === serviceToDelete,
                  );
                  return dealer ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: localization("confirm_delete_dealer").replace(
                          "{0}",
                          `<strong>${dealer.name}</strong>`,
                        ),
                      }}
                    />
                  ) : (
                    localization("confirm_delete_dealer_generic")
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-danger"
                  onClick={confirmDeleteService}
                >
                  {localization("delete_shop")}
                </button>
                <button
                  className="btn btn-cancel"
                  onClick={() => setShowDeleteModal(false)}
                >
                  {localization("cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DealerShipListing;
